import { useState, useEffect, useCallback } from 'react';
import api from '../api';
import toast from 'react-hot-toast';
import { UtensilsCrossed, Plus, Save, Trash2, RefreshCw, Printer, ExternalLink } from 'lucide-react';
import { loadPosPrinting, printPosTest } from '../utils/posPrinter';

const money = (c) => '$' + (Number(c || 0) / 100).toFixed(2);
const toCents = (d) => Math.round((parseFloat(d) || 0) * 100);
const STATIONS = ['kitchen', 'bar', 'none'];
const blankItem = { name: '', categoryId: '', priceDollars: '', taxable: true, station: 'kitchen', stockQty: '', available: true, active: true };

export default function PosMenuManager() {
  const [location, setLocation] = useState(null);
  const [taxPct, setTaxPct] = useState('');
  const [categories, setCategories] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newCat, setNewCat] = useState('');
  const [newItem, setNewItem] = useState(blankItem);
  const [editItem, setEditItem] = useState(null); // {id, ...fields}
  const [printCfg, setPrintCfg] = useState({ printerHost: '', deviceId: 'local_printer', kitchenTicket: true, customerReceipt: true, receiptCopies: 1 });
  const [printSaving, setPrintSaving] = useState(false);
  const [printTesting, setPrintTesting] = useState(false);

  useEffect(() => { loadPosPrinting().then((c) => { if (c) setPrintCfg(c); }); }, []);

  const savePrinting = async () => {
    setPrintSaving(true);
    try {
      const res = await api.put('/pos/printing', {
        printerHost: printCfg.printerHost || '',
        deviceId: printCfg.deviceId || 'local_printer',
        kitchenTicket: !!printCfg.kitchenTicket,
        customerReceipt: !!printCfg.customerReceipt,
        receiptCopies: Number(printCfg.receiptCopies) || 1,
      });
      setPrintCfg(res.data);
      toast.success('Printer settings saved');
    } catch (e) { toast.error(e.response?.data?.error || 'Could not save printer settings'); }
    finally { setPrintSaving(false); }
  };
  const testPrint = async () => {
    setPrintTesting(true);
    const r = await printPosTest(printCfg.printerHost, location, printCfg.deviceId);
    setPrintTesting(false);
    if (r.ok) toast.success('Test slip printed');
    else toast.error(r.error || 'Test print failed', { duration: 12000 });
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [locRes, catRes, itemRes] = await Promise.all([
        api.get('/pos/locations'),
        api.get('/pos/categories'),
        api.get('/pos/items?includeInactive=1'),
      ]);
      const loc = (locRes.data.locations || [])[0] || null;
      setLocation(loc);
      setTaxPct(loc ? (Number(loc.taxRateBps || 0) / 100).toString() : '');
      setCategories(catRes.data.categories || []);
      setItems(itemRes.data.items || []);
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed to load menu');
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const catName = (id) => categories.find((c) => c.id === id)?.name || '—';

  const saveTax = async () => {
    if (!location) return;
    try {
      await api.put(`/pos/locations/${location.id}`, { taxRateBps: Math.round((parseFloat(taxPct) || 0) * 100) });
      toast.success('Tax rate saved');
      load();
    } catch (e) { toast.error(e.response?.data?.error || 'Failed'); }
  };

  const addCategory = async () => {
    if (!newCat.trim()) return;
    try {
      await api.post('/pos/categories', { name: newCat.trim(), sortOrder: categories.length });
      setNewCat(''); toast.success('Category added'); load();
    } catch (e) { toast.error(e.response?.data?.error || 'Failed'); }
  };
  const toggleCategory = async (c) => {
    try { await api.put(`/pos/categories/${c.id}`, { active: !c.active }); load(); }
    catch (e) { toast.error(e.response?.data?.error || 'Failed'); }
  };

  const addItem = async () => {
    if (!newItem.name.trim()) return toast.error('Item name required');
    try {
      await api.post('/pos/items', {
        name: newItem.name.trim(), categoryId: newItem.categoryId || null,
        priceCents: toCents(newItem.priceDollars), taxable: newItem.taxable,
        station: newItem.station, available: newItem.available, active: newItem.active,
        stockQty: newItem.stockQty === '' ? null : parseInt(newItem.stockQty, 10),
      });
      setNewItem(blankItem); toast.success('Item added'); load();
    } catch (e) { toast.error(e.response?.data?.error || 'Failed'); }
  };
  const saveItem = async () => {
    try {
      await api.put(`/pos/items/${editItem.id}`, {
        name: editItem.name.trim(), categoryId: editItem.categoryId || null,
        priceCents: toCents(editItem.priceDollars), taxable: editItem.taxable,
        station: editItem.station, available: editItem.available, active: editItem.active,
        stockQty: editItem.stockQty === '' || editItem.stockQty == null ? null : parseInt(editItem.stockQty, 10),
      });
      setEditItem(null); toast.success('Item saved'); load();
    } catch (e) { toast.error(e.response?.data?.error || 'Failed'); }
  };
  const startEdit = (it) => setEditItem({
    id: it.id, name: it.name, categoryId: it.categoryId || '',
    priceDollars: (Number(it.priceCents || 0) / 100).toString(), taxable: it.taxable,
    station: it.station, stockQty: it.stockQty == null ? '' : String(it.stockQty),
    available: it.available, active: it.active,
  });
  const toggle86 = async (it) => {
    try { await api.post(`/pos/items/${it.id}/availability`, { available: !it.available }); load(); }
    catch (e) { toast.error(e.response?.data?.error || 'Failed'); }
  };

  return (
    <div className="p-6 max-w-6xl mx-auto text-gray-100">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold flex items-center gap-2"><UtensilsCrossed className="w-6 h-6" /> POS Menu</h1>
        <button onClick={load} className="flex items-center gap-2 px-3 py-2 bg-gray-700 rounded-lg hover:bg-gray-600">
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </button>
      </div>

      {/* Tax rate */}
      <div className="bg-gray-800 rounded-xl p-4 mb-6 flex items-end gap-3">
        <div>
          <label className="block text-sm text-gray-400 mb-1">Sales tax rate (%) — {location?.name || 'location'}</label>
          <input value={taxPct} onChange={(e) => setTaxPct(e.target.value)} placeholder="8.75"
            className="w-28 bg-gray-900 border border-gray-700 rounded-lg px-3 py-2" />
        </div>
        <button onClick={saveTax} className="flex items-center gap-2 px-3 py-2 bg-emerald-600 rounded-lg hover:bg-emerald-500"><Save className="w-4 h-4" /> Save</button>
      </div>

      {/* Printer */}
      <div className="bg-gray-800 rounded-xl p-4 mb-6">
        <h2 className="font-semibold mb-1 flex items-center gap-2"><Printer className="w-4 h-4" /> Printer (Epson ePOS)</h2>
        <p className="text-xs text-gray-400 mb-3">
          Kitchen tickets and receipts print automatically when an order is paid. Each register device must be able to reach the
          printer through the firewall, and must accept the printer&apos;s certificate once:
          {printCfg.printerHost ? (
            <a href={`https://${printCfg.printerHost}/`} target="_blank" rel="noreferrer" className="ml-1 text-indigo-300 underline inline-flex items-center gap-1">
              open https://{printCfg.printerHost}/ <ExternalLink className="w-3 h-3" />
            </a>
          ) : ' set the printer IP first.'}
        </p>
        <div className="flex flex-wrap items-end gap-4">
          <div>
            <label className="block text-sm text-gray-400 mb-1">Printer IP</label>
            <input value={printCfg.printerHost || ''} onChange={(e) => setPrintCfg({ ...printCfg, printerHost: e.target.value })} placeholder="192.168.20.50"
              className="w-48 bg-gray-900 border border-gray-700 rounded-lg px-3 py-2" />
          </div>
          <div>
            <label className="block text-sm text-gray-400 mb-1">ePOS Device ID</label>
            <input value={printCfg.deviceId || ''} onChange={(e) => setPrintCfg({ ...printCfg, deviceId: e.target.value })} placeholder="local_printer"
              className="w-44 bg-gray-900 border border-gray-700 rounded-lg px-3 py-2" />
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={!!printCfg.kitchenTicket} onChange={(e) => setPrintCfg({ ...printCfg, kitchenTicket: e.target.checked })} />
            Kitchen ticket
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={!!printCfg.customerReceipt} onChange={(e) => setPrintCfg({ ...printCfg, customerReceipt: e.target.checked })} />
            Customer receipt
          </label>
          <div>
            <label className="block text-sm text-gray-400 mb-1">Receipt copies</label>
            <select value={printCfg.receiptCopies || 1} onChange={(e) => setPrintCfg({ ...printCfg, receiptCopies: Number(e.target.value) })}
              className="bg-gray-900 border border-gray-700 rounded-lg px-3 py-2">
              {[1, 2, 3].map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>
          <button onClick={savePrinting} disabled={printSaving} className="flex items-center gap-2 px-3 py-2 bg-emerald-600 rounded-lg hover:bg-emerald-500 disabled:opacity-50"><Save className="w-4 h-4" /> {printSaving ? 'Saving...' : 'Save'}</button>
          <button onClick={testPrint} disabled={printTesting || !printCfg.printerHost} className="flex items-center gap-2 px-3 py-2 bg-gray-700 rounded-lg hover:bg-gray-600 disabled:opacity-50"><Printer className="w-4 h-4" /> {printTesting ? 'Printing...' : 'Test print'}</button>
        </div>
      </div>

      {/* Categories */}
      <div className="bg-gray-800 rounded-xl p-4 mb-6">
        <h2 className="font-semibold mb-3">Categories</h2>
        <div className="flex flex-wrap gap-2 mb-3">
          {categories.map((c) => (
            <button key={c.id} onClick={() => toggleCategory(c)}
              className={`px-3 py-1 rounded-full text-sm ${c.active ? 'bg-indigo-600' : 'bg-gray-700 text-gray-400 line-through'}`}>
              {c.name}
            </button>
          ))}
          {!categories.length && <span className="text-gray-500 text-sm">No categories yet</span>}
        </div>
        <div className="flex gap-2">
          <input value={newCat} onChange={(e) => setNewCat(e.target.value)} placeholder="New category"
            className="bg-gray-900 border border-gray-700 rounded-lg px-3 py-2" />
          <button onClick={addCategory} className="flex items-center gap-2 px-3 py-2 bg-indigo-600 rounded-lg hover:bg-indigo-500"><Plus className="w-4 h-4" /> Add</button>
        </div>
      </div>

      {/* Add item */}
      <div className="bg-gray-800 rounded-xl p-4 mb-6">
        <h2 className="font-semibold mb-3">Add item</h2>
        <div className="grid grid-cols-1 md:grid-cols-6 gap-2 items-end">
          <div className="md:col-span-2"><label className="block text-xs text-gray-400">Name</label>
            <input value={newItem.name} onChange={(e) => setNewItem({ ...newItem, name: e.target.value })} className="w-full bg-gray-900 border border-gray-700 rounded-lg px-2 py-2" /></div>
          <div><label className="block text-xs text-gray-400">Category</label>
            <select value={newItem.categoryId} onChange={(e) => setNewItem({ ...newItem, categoryId: e.target.value })} className="w-full bg-gray-900 border border-gray-700 rounded-lg px-2 py-2">
              <option value="">—</option>{categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</select></div>
          <div><label className="block text-xs text-gray-400">Price $</label>
            <input value={newItem.priceDollars} onChange={(e) => setNewItem({ ...newItem, priceDollars: e.target.value })} className="w-full bg-gray-900 border border-gray-700 rounded-lg px-2 py-2" /></div>
          <div><label className="block text-xs text-gray-400">Station</label>
            <select value={newItem.station} onChange={(e) => setNewItem({ ...newItem, station: e.target.value })} className="w-full bg-gray-900 border border-gray-700 rounded-lg px-2 py-2">{STATIONS.map((s) => <option key={s} value={s}>{s}</option>)}</select></div>
          <div><label className="block text-xs text-gray-400">Stock (blank=∞)</label>
            <input value={newItem.stockQty} onChange={(e) => setNewItem({ ...newItem, stockQty: e.target.value })} className="w-full bg-gray-900 border border-gray-700 rounded-lg px-2 py-2" /></div>
        </div>
        <div className="flex items-center gap-4 mt-3">
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={newItem.taxable} onChange={(e) => setNewItem({ ...newItem, taxable: e.target.checked })} /> Taxable</label>
          <button onClick={addItem} className="flex items-center gap-2 px-3 py-2 bg-indigo-600 rounded-lg hover:bg-indigo-500"><Plus className="w-4 h-4" /> Add item</button>
        </div>
      </div>

      {/* Items list */}
      <div className="bg-gray-800 rounded-xl p-4">
        <h2 className="font-semibold mb-3">Items ({items.length})</h2>
        <div className="space-y-2">
          {items.map((it) => editItem && editItem.id === it.id ? (
            <div key={it.id} className="grid grid-cols-1 md:grid-cols-7 gap-2 items-center bg-gray-900 rounded-lg p-2">
              <input value={editItem.name} onChange={(e) => setEditItem({ ...editItem, name: e.target.value })} className="md:col-span-2 bg-gray-800 border border-gray-700 rounded px-2 py-1" />
              <select value={editItem.categoryId} onChange={(e) => setEditItem({ ...editItem, categoryId: e.target.value })} className="bg-gray-800 border border-gray-700 rounded px-2 py-1"><option value="">—</option>{categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</select>
              <input value={editItem.priceDollars} onChange={(e) => setEditItem({ ...editItem, priceDollars: e.target.value })} className="bg-gray-800 border border-gray-700 rounded px-2 py-1" />
              <select value={editItem.station} onChange={(e) => setEditItem({ ...editItem, station: e.target.value })} className="bg-gray-800 border border-gray-700 rounded px-2 py-1">{STATIONS.map((s) => <option key={s} value={s}>{s}</option>)}</select>
              <input value={editItem.stockQty} onChange={(e) => setEditItem({ ...editItem, stockQty: e.target.value })} placeholder="∞" className="bg-gray-800 border border-gray-700 rounded px-2 py-1" />
              <div className="flex gap-2">
                <button onClick={saveItem} className="px-2 py-1 bg-emerald-600 rounded"><Save className="w-4 h-4" /></button>
                <button onClick={() => setEditItem(null)} className="px-2 py-1 bg-gray-600 rounded text-sm">Cancel</button>
              </div>
            </div>
          ) : (
            <div key={it.id} className={`flex items-center justify-between bg-gray-900 rounded-lg p-3 ${!it.active ? 'opacity-50' : ''}`}>
              <div>
                <span className="font-medium">{it.name}</span>
                <span className="text-gray-400 text-sm ml-2">{catName(it.categoryId)} · {money(it.priceCents)} · {it.station}{it.stockQty != null ? ` · ${it.stockQty} left` : ''}{it.taxable ? '' : ' · no tax'}</span>
                {!it.available && <span className="ml-2 text-xs px-2 py-0.5 bg-red-900 text-red-300 rounded">86'd</span>}
              </div>
              <div className="flex gap-2">
                <button onClick={() => toggle86(it)} className={`px-2 py-1 rounded text-sm ${it.available ? 'bg-amber-700 hover:bg-amber-600' : 'bg-emerald-700 hover:bg-emerald-600'}`}>{it.available ? "86" : 'Un-86'}</button>
                <button onClick={() => startEdit(it)} className="px-2 py-1 bg-gray-700 rounded text-sm hover:bg-gray-600">Edit</button>
              </div>
            </div>
          ))}
          {!items.length && <div className="text-gray-500 text-sm">No items yet — add one above.</div>}
        </div>
      </div>
    </div>
  );
}
